import { fetchTutors } from "../util/fetchTutors";
import getOrSetCookie from "../util/getOrSetCookie";
import TutorListing from "../components/TutorListing";
import getVerifiedSlugs from "../util/getVerifiedSlugs";
import getRedirectDestination from "../util/getRedirectDestination";
import { fetchPageSEOData } from "../util/pageMetadata";
import { SubjectAndLevel } from "../models/SubjectAndLevel";
import { Slugs } from "../models/Slugs";
import { TutorListingMetadata } from "../models/TutorListingMetadata";
import { GetServerSidePropsContext, GetServerSideProps } from "next";
import Head from "next/head";
import { HeadDataLayerDimensions } from "@mytutor/shared-react-web-components/lib/SiteTracking";
import React, { useContext, useEffect } from "react";
import { UserContext, validateUserInfo } from "../context/userContext";

import {
  CookiesTypes,
  Pagination,
  TutorType,
  TutorAggregate,
  UrlParameters,
} from "../util/fetchTutors/types";
import { serialize } from "cookie";
import { SnowplowPageTracker } from "../util/snowplowPageTracker/SnowplowPageTracker";

type Props = {
  tutorsList: TutorType[] | null;
  paginationData: Pagination;
  tutorAggregate: TutorAggregate;
  metadata: TutorListingMetadata;
  subjectAndLevel: SubjectAndLevel;
  urlParameters: UrlParameters;
  userInfo: string | null;
  cookies: CookiesTypes | null;
};

const TutorView = ({
  tutorsList,
  paginationData,
  tutorAggregate,
  metadata,
  subjectAndLevel,
  urlParameters,
  userInfo,
  cookies,
}: Props) => {
  const { addUserDetails, isAuthenticated } = useContext(UserContext);
  const validatedUserInfo = validateUserInfo(userInfo);

  useEffect(() => {
    addUserDetails(validatedUserInfo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const formatString = (value: string | undefined) =>
    value ? `"${value}"` : null;

  return (
    <>
      <Head>
        <HeadDataLayerDimensions
          pageType="view-tutors"
          userId={formatString(validatedUserInfo?.id)}
          userType={formatString(validatedUserInfo?.type)}
        />
      </Head>
      <SnowplowPageTracker title="zeus--tutor-directory--view-tutors-page" />
      
      <TutorListing
        tutorsList={tutorsList}
        paginationData={paginationData}
        tutorAggregate={tutorAggregate}
        metadata={metadata}
        subjectAndLevel={subjectAndLevel}
        urlParameters={urlParameters}
        cookies={cookies}
        isAuthenticated={isAuthenticated}
      />
      
    </>
  );
};

const seoSubjectCombinations = [
  "Physics-Maths",
  "Maths-Science",
  "Chemistry-Maths",
  "Maths-English",
];
const isSubjectCombination = (input: string) => {
  return seoSubjectCombinations.includes(input);
};

const invalidSlug = (verifiedSlugs: Slugs) => {
  return verifiedSlugs.additionalSlug;
};

type contextQueryTypes = {
  slug: string | string[];
  page: string | string[];
  pageSize: string | string[];
  price: string;
  gender: string | string[];
  availability: string;
  additionalSubject: string | string[];
  category: string;
};

const toQueryString = (params: Record<string, string | null | undefined>) => {
  const queryString = new URLSearchParams();

  for (const key in params) {
    if (
      params[key] !== null &&
      params[key] !== undefined &&
      params[key] !== ""
    ) {
      // Ensure the value is not null
      queryString.append(key, params[key]);
    }
  }

  const result = queryString.toString();
  return result ? `?${result}` : "";
};

const restoreFilters = (context: GetServerSidePropsContext, page) => {
  // get urlParameters from userFilters cookie
  let userFiltersCookieString = getOrSetCookie(context, "userFilters");

  // Remove "userFilters=" from the start of userFiltersCookieString
  userFiltersCookieString = userFiltersCookieString.slice(12);
  const userFiltersCookie = JSON.parse(userFiltersCookieString);

  const redirectURLSubject = userFiltersCookie.subject
    ? "/" + userFiltersCookie.subject
    : "";
  const redirectURLLevel = userFiltersCookie.level ?? "";
  const redirectURL =
    redirectURLSubject || redirectURLLevel
      ? `${redirectURLSubject}/${redirectURLLevel}`
      : "/";

  const filtersQueryParams = toQueryString({
    price: userFiltersCookie.price,
    gender: userFiltersCookie.gender,
    availability: userFiltersCookie.availability,
    category: userFiltersCookie.category
  });
  const pageParam = filtersQueryParams ? `&page=${page}` : `?page=${page}`;

  return {
    redirect: {
      destination: `${redirectURL}${filtersQueryParams}${pageParam}`,
      permanent: false,
    },
  };
};

const getFiltersCookieSerialized = (
  context: GetServerSidePropsContext,
  filteredSlugs: SubjectAndLevel,
  urlParameters: UrlParameters
) => {
  if (context.req.url.includes("view-tutors")) return;

  const urlParametersString = JSON.stringify({
    ...filteredSlugs,
    price: urlParameters.price,
    gender: urlParameters.gender,
    availability: urlParameters.availability,
    category: urlParameters.category
  });
  return serialize("userFilters", urlParametersString, {
    maxAge: 3600, // 1hr cookie to allow time to send message to tutor
    path: "/",
  });
};

const shouldRestoreFilters = (context: GetServerSidePropsContext) =>
  context.query.restoreFilters === "1";

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
  // eslint-disable-next-line
) => {
  const {
    slug,
    page,
    pageSize,
    price,
    gender,
    availability,
    additionalSubject,
    category
  } = context.query as contextQueryTypes;

  const verifiedSlugs = getVerifiedSlugs(slug);

  if (invalidSlug(verifiedSlugs)) {
    const parameters = context.resolvedUrl.split("?")[1] || null;
    const redirectDestination = getRedirectDestination(
      verifiedSlugs,
      parameters
    );
    return {
      redirect: {
        destination: redirectDestination,
        permanent: true,
      },
    };
  }

  let filterCookies: string;
  const filteredSlugs: SubjectAndLevel = {
    subject: verifiedSlugs.subject,
    level: verifiedSlugs.level,
  };

  const metadataResponse = await fetchPageSEOData(filteredSlugs);
  const metadata: TutorListingMetadata = metadataResponse.data.fields;
  const urlParameters: UrlParameters = {
    price: price ? price : null,
    gender: gender ? gender : null,
    availability: availability ? availability : null,
    additionalSubject: additionalSubject ? additionalSubject : null,
    category: category ? category : null,
    page: page ? page : "1",
    pageSize: pageSize ? pageSize : "6",
  };

  if (shouldRestoreFilters(context)) {
    return restoreFilters(context, urlParameters.page);
  } else {
    filterCookies = getFiltersCookieSerialized(
      context,
      filteredSlugs,
      urlParameters
    );
  }

  if (isSubjectCombination(filteredSlugs.subject)) {
    [filteredSlugs.subject, urlParameters.additionalSubject] =
      filteredSlugs.subject.split("-");
  }

  const sessionCookie = getOrSetCookie(context, "JSESSIONID");
  const awsCookie = getOrSetCookie(context, "AWSALB");
  const awsCorsCookie = getOrSetCookie(context, "AWSALBCORS");
  const rankingTestCookie = getOrSetCookie(context, "mytutor-ranking-ab");

  const cookies: CookiesTypes = {
    sessionCookie,
    awsCookie,
    awsCorsCookie,
    rankingTestCookie,
  };

  const response = await fetchTutors({
    subject: filteredSlugs.subject,
    level: filteredSlugs.level,
    pageNumber: urlParameters.page,
    pageSize: urlParameters.pageSize,
    price: urlParameters.price,
    gender: urlParameters.gender,
    availability: urlParameters.availability,
    additionalSubject: urlParameters.additionalSubject,
    category: urlParameters.category,
    cookies,
  });

  context.res.setHeader("Set-Cookie", [
    `${response.data.setCookieHeader};path=/`,
    filterCookies,
  ]);

  if (response.data.redirectURL) {
    return {
      redirect: {
        destination: response.data.redirectURL,
        permanent: false,
      },
    };
  }

  // redirect to page 1 if user is not authenticated and page > 1
  if (response.data.userHeaders === null && Number(urlParameters.page) > 1) {
    const redirectURL = new URL(
      `/view-tutors/${context.req.url}`,
      `${process.env.NEXT_PUBLIC_ZEUS_URL}`
    );
    redirectURL.searchParams.set("page", "1");
    return {
      redirect: {
        destination: redirectURL.toString(),
        permanent: false,
      },
    };
  }

  const tutorAggregate: TutorAggregate = response.data.tutorAggregate || null;

  return {
    props: {
      tutorsList: response.data.tutors || null,
      paginationData: response.data.pagination || null,
      tutorAggregate: tutorAggregate,
      subjectAndLevel: filteredSlugs,
      urlParameters: urlParameters,
      metadata: metadata,
      userInfo: response.data.userHeaders || null,
      cookies,
    },
  };
};

export default TutorView;
